import { getI18n } from "@hoppscotch/common/modules/i18n"
import {
  BANNER_PRIORITY_MEDIUM,
  BannerContent,
  BannerService,
  BannerType,
} from "@hoppscotch/common/services/banner.service"
import axios from "axios"
import { differenceInDays, parseISO } from "date-fns"
import { Service } from "dioc"

const daysRemaining = (expiryDate: string) =>
  differenceInDays(parseISO(expiryDate), new Date())

/**
 * Returns the banner type based on the number of days remaining for the license to expire
 */
const getLicenseBannerType = (daysRemaining: number): BannerType => {
  if (daysRemaining <= 0) {
    return "error"
  }
  if (daysRemaining < 4) {
    return "warning"
  }
  return "info"
}

/**
 * Returns a banner to be displayed based on the license status
 */
const getLicenseBanner = (data: any): BannerContent => {
  const isLicenseValid =
    data.status === "valid" || data.status === "valid_but_cancelled"
  return {
    type: getLicenseBannerType(
      isLicenseValid ? daysRemaining(data.validUntilISO) : 0
    ),
    text: (t: ReturnType<typeof getI18n>) => {
      if (isLicenseValid) {
        const days = daysRemaining(data.validUntilISO)
        const daysText =
          days === 1
            ? t("license.expiring_in_day")
            : t("license.expiring_in_days")

        const isCancelled = data.status === "valid_but_cancelled"

        if (isCancelled) {
          return t("license.expiring", {
            days: `${days} ${daysText}`,
          })
        }

        return t("license.renewing", {
          days: `${days} ${daysText}`,
        })
      }
      return t("license.expired")
    },
    score: BANNER_PRIORITY_MEDIUM,
  }
}

/**
 * Service for managing licensing information.
 */
export class LicensingService extends Service {
  /**
   * The ID of the licensing service.
   */
  public static readonly ID = "LICENSING_SERVICE"

  private readonly banner = this.bind(BannerService)

  onServiceInit() {
    this.getLicenseStatus()
  }

  /**
   * Retrieves the license status from the backend and displays a banner if:
   * - The license is active but expiring within 11 days (renewal reminder)
   * - The subscription is past due
   * - The subscription is cancelled (shown unconditionally to prompt resubscription)
   */
  public async getLicenseStatus() {
    try {
      const res = await axios.get(
        `${import.meta.env.VITE_BACKEND_API_URL}/license/status`,
        {
          withCredentials: true,
        }
      )

      if (
        (res.data.status === "valid" &&
          daysRemaining(res.data.validUntilISO) < 11) ||
        res.data.status === "valid_but_past_due" ||
        res.data.status === "valid_but_cancelled"
      ) {
        this.banner.showBanner(getLicenseBanner(res.data))
      }
    } catch (error) {
      console.error(error)
    }
  }
}
